import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, Minus, Plus, Truck, RotateCcw, Shield } from "lucide-react";
import { fetchProduct } from "@/lib/shopify";
import { useStore, formatINR } from "@/context/StoreContext";
import ProductCard from "@/components/product/ProductCard";
import NotFound from "@/pages/NotFound";

const ProductDetail = () => {
  const { handle } = useParams();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [zoom, setZoom] = useState({ active: false, x: 50, y: 50 });
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  const { addToCart, toggleWishlist, isWishlisted } = useStore();

  useEffect(() => {
    let alive = true;

    setLoading(true);
    setNotFound(false);
    setActiveImg(0);
    setSize(null);
    setQty(1);

    (async () => {
      try {
        const { product: p, related: r } = await fetchProduct(handle);

        if (!alive) return;

        setProduct(p);
        setRelated(r || []);

        if (p?.sizes?.length === 1) {
          setSize(p.sizes[0]);
        }
      } catch (err) {
        if (!alive) return;

        if (err?.response?.status === 404) {
          setNotFound(true);
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [handle]);

  if (notFound) {
    return <NotFound />;
  }

  if (loading || !product) {
    return (
      <div
        data-testid="product-loading"
        className="max-w-[1400px] mx-auto px-6 md:px-12 py-16"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 animate-pulse">
          <div className="lg:col-span-7 aspect-[4/5] bg-[color:var(--vw-bg-alt)]" />

          <div className="lg:col-span-5 space-y-4">
            <div className="h-4 bg-[color:var(--vw-bg-alt)] w-24" />
            <div className="h-10 bg-[color:var(--vw-bg-alt)] w-3/4" />
            <div className="h-6 bg-[color:var(--vw-bg-alt)] w-32" />
            <div className="h-20 bg-[color:var(--vw-bg-alt)]" />
          </div>
        </div>
      </div>
    );
  }

  const onSale =
    product.compareAtPrice &&
    product.compareAtPrice > product.price;

  const wished = isWishlisted(product.handle);

  const media =
    product.media && product.media.length > 0
      ? product.media
      : (product.images || []).map((src) => ({
          type: "image",
          url: src,
          preview: src,
        }));

  const activeMedia = media[activeImg];

  return (
    <div
      data-testid={`product-page-${product.handle}`}
      className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 md:py-16"
    >
      {/* Breadcrumbs */}
      <nav className="overline text-[color:var(--vw-ink-soft)] mb-8">
        <Link
          to="/"
          className="hover:text-[color:var(--vw-ink)]"
        >
          Home
        </Link>

        <span className="mx-2">/</span>

        <Link
          to="/shop"
          className="hover:text-[color:var(--vw-ink)]"
        >
          Shop
        </Link>

        <span className="mx-2">/</span>

        <span className="text-[color:var(--vw-ink)]">
          {product.title}
        </span>
      </nav>

      {/* Product */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">

        {/* ================= PRODUCT GALLERY ================= */}
        <div className="lg:col-span-7">

          {/* Desktop Gallery */}
          <div className="hidden md:flex gap-4 w-full">

            {/* Thumbnails */}
            {media.length > 1 && (
              <div className="flex flex-col gap-3 w-20 flex-shrink-0">
                {media.map((item, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`thumb-${i}`}
                    onClick={() => {
                      setActiveImg(i);
                      setZoom({
                        active: false,
                        x: 50,
                        y: 50,
                      });
                    }}
                    className={`aspect-[4/5] overflow-hidden border ${
                      activeImg === i
                        ? "border-[color:var(--vw-ink)]"
                        : "border-transparent hover:border-[color:var(--vw-beige-dark)]"
                    }`}
                  >
                    {item.type === "video" ? (
                      <div className="relative w-full h-full">
                        <video
                          src={item.url}
                          poster={item.preview || undefined}
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                          preload="metadata"
                        />

                        <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center text-black text-xs">
                            ▶
                          </span>
                        </span>
                      </div>
                    ) : (
                      <img
                        src={item.url}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Main Media */}
            <div
              data-testid="main-image"
              className={`relative flex-1 aspect-[4/5] overflow-hidden bg-[color:var(--vw-bg-alt)] ${
                activeMedia?.type === "video"
                  ? ""
                  : "cursor-zoom-in"
              }`}
              onMouseEnter={() => {
                if (activeMedia?.type !== "video") {
                  setZoom((z) => ({
                    ...z,
                    active: true,
                  }));
                }
              }}
              onMouseLeave={() => {
                if (activeMedia?.type !== "video") {
                  setZoom((z) => ({
                    ...z,
                    active: false,
                  }));
                }
              }}
              onMouseMove={(e) => {
                if (activeMedia?.type === "video") {
                  return;
                }

                const rect =
                  e.currentTarget.getBoundingClientRect();

                setZoom({
                  active: true,
                  x:
                    ((e.clientX - rect.left) /
                      rect.width) *
                    100,
                  y:
                    ((e.clientY - rect.top) /
                      rect.height) *
                    100,
                });
              }}
            >
              {activeMedia?.type === "video" ? (
                <video
                  key={activeMedia.url}
                  src={activeMedia.url}
                  poster={activeMedia.preview || undefined}
                  className="w-full h-full object-cover"
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                />
              ) : (
                activeMedia?.url && (
                  <img
                    src={activeMedia.url}
                    alt={product.title}
                    className="w-full h-full object-cover transition-transform duration-300"
                    style={
                      zoom.active
                        ? {
                            transform: "scale(1.8)",
                            transformOrigin: `${zoom.x}% ${zoom.y}%`,
                          }
                        : {
                            transform: "scale(1)",
                          }
                    }
                  />
                )
              )}

              {onSale && (
                <span className="absolute top-4 left-4 overline bg-[color:var(--vw-bg)] px-3 py-1.5">
                  Sale
                </span>
              )}
            </div>
          </div>

          {/* Mobile Gallery */}
          <div className="md:hidden w-full">

            <div
              className="flex overflow-x-auto snap-x snap-mandatory"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {media.map((item, i) => (
                <div
                  key={i}
                  className="w-full flex-shrink-0 aspect-[4/5] snap-center overflow-hidden bg-[color:var(--vw-bg-alt)] relative"
                >
                  {item.type === "video" ? (
                    <>
                      <video
                        src={item.url}
                        poster={item.preview || undefined}
                        className="w-full h-full object-cover"
                        controls
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                      />

                      <span className="absolute top-4 left-4 overline bg-[color:var(--vw-bg)] px-3 py-1.5 pointer-events-none">
                        Video
                      </span>
                    </>
                  ) : (
                    <img
                      src={item.url}
                      alt={`${product.title} ${i + 1}`}
                      className="w-full h-full object-cover"
                      loading={i === 0 ? "eager" : "lazy"}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Mobile dots */}
            {media.length > 1 && (
              <div className="flex justify-center gap-1.5 mt-3">
                {media.map((_, i) => (
                  <span
                    key={i}
                    className={`w-1.5 h-1.5 rounded-full ${
                      i === activeImg
                        ? "bg-[color:var(--vw-ink)]"
                        : "bg-[color:var(--vw-ink-soft)] opacity-60"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ================= PRODUCT INFORMATION ================= */}
        <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">

          <p className="overline text-[color:var(--vw-ink-soft)]">
            Vastra West
          </p>

          <h1
            data-testid="product-title"
            className="font-serif-display text-4xl md:text-5xl italic mt-3 leading-tight"
          >
            {product.title}
          </h1>

          {/* Price */}
          <div className="mt-4 flex items-baseline gap-3">
            <span className="font-serif-display text-2xl">
              {formatINR(product.price)}
            </span>

            {onSale && (
              <span className="text-[color:var(--vw-ink-soft)] line-through text-sm">
                {formatINR(product.compareAtPrice)}
              </span>
            )}

            <span className="text-xs font-utility text-[color:var(--vw-ink-soft)]">
              MRP incl. of taxes
            </span>
          </div>

          {/* Description */}
          {product.description && (
            <p className="mt-6 text-[color:var(--vw-ink-soft)] leading-relaxed whitespace-pre-line">
              {product.description}
            </p>
          )}

          {/* Sizes */}
          {product.sizes?.length > 0 && (
            <div className="mt-8">

              <div className="flex items-center justify-between mb-3">

                <p className="overline">
                  Size{" "}
                  {size && (
                    <span className="text-[color:var(--vw-ink-soft)]">
                      — {size}
                    </span>
                  )}
                </p>

                <button
                  type="button"
                  onClick={() => setShowSizeGuide(true)}
                  className="overline text-[color:var(--vw-ink-soft)] hover:text-[color:var(--vw-ink)]"
                >
                  Size Guide
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => {
                  const variant = product.variants?.find(
                    (v) => v.size === s
                  );

                  const disabled =
                    variant && variant.available === false;

                  return (
                    <button
                      key={s}
                      type="button"
                      data-testid={`size-${s}`}
                      disabled={disabled}
                      onClick={() => setSize(s)}
                      className={`w-14 h-12 border font-utility text-sm transition-colors ${
                        size === s
                          ? "border-[color:var(--vw-ink)] bg-[color:var(--vw-ink)] text-[color:var(--vw-bg)]"
                          : "border-[color:var(--vw-beige-dark)] hover:border-[color:var(--vw-ink)]"
                      } ${
                        disabled
                          ? "line-through opacity-40 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quantity / Cart / Wishlist */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3">

            <div className="flex items-center border border-[color:var(--vw-beige-dark)]">

              <button
                type="button"
                data-testid="qty-dec"
                aria-label="Decrease"
                onClick={() =>
                  setQty((q) => Math.max(1, q - 1))
                }
                className="p-3 hover:bg-[color:var(--vw-bg-alt)]"
              >
                <Minus size={14} />
              </button>

              <span
                data-testid="qty-value"
                className="w-10 text-center font-utility"
              >
                {qty}
              </span>

              <button
                type="button"
                data-testid="qty-inc"
                aria-label="Increase"
                onClick={() => setQty((q) => q + 1)}
                className="p-3 hover:bg-[color:var(--vw-bg-alt)]"
              >
                <Plus size={14} />
              </button>
            </div>

            <button
              type="button"
              data-testid="add-to-cart-btn"
              onClick={() => addToCart(product, size, qty)}
              disabled={product.availableForSale === false}
              className="flex-1 overline bg-[color:var(--vw-ink)] text-[color:var(--vw-bg)] py-4 hover:opacity-90 transition-opacity disabled:opacity-40"
            >
              {product.availableForSale === false
                ? "Sold Out"
                : "Add to Bag"}
            </button>

            <button
              type="button"
              data-testid="wishlist-toggle"
              aria-label="Wishlist"
              onClick={() => toggleWishlist(product)}
              className="w-12 h-12 border border-[color:var(--vw-beige-dark)] flex items-center justify-center hover:border-[color:var(--vw-ink)]"
            >
              <Heart
                size={16}
                className={
                  wished
                    ? "fill-[color:var(--vw-ink)]"
                    : ""
                }
              />
            </button>
          </div>

          {/* Benefits */}
          <ul className="mt-10 grid grid-cols-3 gap-4 pt-6">

            <li className="text-center">
              <Truck
                size={18}
                className="mx-auto mb-2 text-[color:var(--vw-ink-soft)]"
              />
              <p className="overline text-[color:var(--vw-ink-soft)] leading-tight">
                Free above ₹999
              </p>
            </li>

            <li className="text-center">
              <RotateCcw
                size={18}
                className="mx-auto mb-2 text-[color:var(--vw-ink-soft)]"
              />
              <p className="overline text-[color:var(--vw-ink-soft)] leading-tight">
                7-day returns
              </p>
            </li>

            <li className="text-center">
              <Shield
                size={18}
                className="mx-auto mb-2 text-[color:var(--vw-ink-soft)]"
              />
              <p className="overline text-[color:var(--vw-ink-soft)] leading-tight">
                Secure checkout
              </p>
            </li>

          </ul>

          {/* Details */}
          {product.details?.length > 0 && (
            <div className="mt-8 pt-6">

              <p className="overline mb-3">
                Details
              </p>

              <ul className="space-y-2 text-sm text-[color:var(--vw-ink-soft)]">
                {product.details.map((d, i) => (
                  <li key={i}>
                    — {d}
                  </li>
                ))}
              </ul>

            </div>
          )}
        </div>
      </div>

      {/* ================= SIZE GUIDE MODAL ================= */}
      {showSizeGuide && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setShowSizeGuide(false)}
        >
          <div
            className="relative max-w-3xl max-h-[90vh] bg-white p-2"
            onClick={(e) => e.stopPropagation()}
          >

            <button
              type="button"
              onClick={() => setShowSizeGuide(false)}
              className="absolute top-3 right-3 w-9 h-9 bg-white border flex items-center justify-center text-xl"
              aria-label="Close Size Guide"
            >
              ×
            </button>

            <img
              src="/Size guide.jpeg"
              alt="Size Guide"
              className="max-w-full max-h-[85vh] object-contain"
            />
          </div>
        </div>
      )}

      {/* ================= RELATED PRODUCTS ================= */}
      {related.length > 0 && (
        <section className="mt-24 md:mt-32">

          <div className="flex items-end justify-between mb-8">

            <h2 className="font-serif-display text-3xl md:text-5xl italic">
              You may also like
            </h2>

            <Link
              to="/shop"
              className="overline hover:opacity-70"
            >
              View all →
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {related.map((p, i) => (
              <ProductCard
                key={p.id || p.handle}
                product={p}
                index={i}
              />
            ))}
          </div>
        </section>
      )}

    </div>
  );
};

export default ProductDetail;